import React, { useEffect, useMemo, useState } from 'react';
import { BookOpen, Save, RefreshCw, ArrowLeft } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { BlockLibrary } from '../components/LessonBuilder/BlockLibrary';
import { LessonCanvas } from '../components/LessonBuilder/LessonCanvas';
import { BlockEditor } from '../components/LessonBuilder/BlockEditor';
import { PageRail } from '../components/LessonBuilder/PageRail';
import { loadLessonDraftFromStorage, useLessonBuilderStore, validateLessonSnapshot } from '../stores/lessonBuilderStore';
import { adminAuthHeaders } from '../services/adminAuthHeaders';

const API_BASE =
  import.meta.env.PROD
    ? 'https://api.clarityofconcept.com/api'
    : (import.meta.env.VITE_LESSON_API_URL || 'http://localhost:8000/api');

const LessonBuilder: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { lessonId: routeLessonId } = useParams<{ lessonId?: string }>();
  const editingLessonId = routeLessonId && routeLessonId !== 'new' ? routeLessonId : null;
  const isEditing = Boolean(editingLessonId);
  const isNewLesson = !isEditing && location.pathname.endsWith('/lessons/new');

  const {
    title,
    setTitle,
    saveDraft,
    publishLesson,
    isSaving,
    isPublishing,
    hasChanges,
    loadDraft,
    pages,
    selectedPageId,
    updatePage,
    ensurePageExists,
    getSnapshot,
    subjectId,
    subjectName,
    category,
    accessLevel,
    curriculumType,
    classLevel,
    chapterTitle,
    difficulty,
    labels,
    applicableYears,
    appliesUntilReplaced,
    setSubject,
    setCategory,
    setAccessLevel,
    setCurriculumType,
    setClassLevel,
    setChapterTitle,
    setDifficulty,
    setLabels,
    setApplicableYears,
    setAppliesUntilReplaced,
    resetLesson,
  } = useLessonBuilderStore();

  const [subjects, setSubjects] = useState<Array<{ id: number; name: string }>>([]);
  const [newSubject, setNewSubject] = useState('');
  const [yearInput, setYearInput] = useState('');
  const [labelInput, setLabelInput] = useState('');
  const [loadingLesson, setLoadingLesson] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      resetLesson();

      if (editingLessonId) {
        setLoadingLesson(true);
        try {
          const res = await fetch(`${API_BASE}/lessons/${editingLessonId}`, {
            headers: adminAuthHeaders(),
          });
          if (!res.ok) {
            const text = await res.text();
            throw new Error(text || `Failed to load lesson ${editingLessonId}`);
          }
          const data = await res.json();
          if (cancelled) return;
          loadDraft({
            lessonId: data.lesson_id || data.id || editingLessonId,
            title: data.title,
            subjectId: data.subject_id ?? null,
            subjectName: data.subject_name ?? '',
            category: data.category,
            accessLevel: data.access_level ?? 'free',
            curriculumType: data.curriculum_type ?? 'general',
            classLevel: data.class_level ?? null,
            chapterTitle: data.chapter_title ?? '',
            difficulty: data.difficulty,
            labels: data.labels || [],
            applicableYears: data.applicable_years || [],
            appliesUntilReplaced: data.applies_until_replaced ?? true,
            pages: data.pages || [],
          });
          setLoadError(null);
        } catch (err: any) {
          if (!cancelled) {
            console.error('Failed to load lesson', err);
            setLoadError(err?.message || 'Failed to load lesson');
            toast.error('Failed to load lesson');
          }
        } finally {
          if (!cancelled) setLoadingLesson(false);
        }
      } else {
        // New lesson: start fresh, clear any lingering draft
        localStorage.removeItem('lesson-builder-draft');
        ensurePageExists();
        setLoadError(null);
      }
    };

    boot();
    return () => {
      cancelled = true;
    };
  }, [editingLessonId, loadDraft, ensurePageExists, resetLesson]);

  useEffect(() => {
    fetchSubjects();
  }, []);

  useEffect(() => {
    if (!selectedPageId) {
      ensurePageExists();
    }
  }, [selectedPageId, ensurePageExists]);

  const selectedPage = pages.find((p) => p.id === selectedPageId) || pages[0];
  const validation = useMemo(() => validateLessonSnapshot(getSnapshot()), [getSnapshot]);
  const hasErrors = validation.errors.length > 0;

  const fetchSubjects = async () => {
    try {
      const res = await fetch(`${API_BASE}/lessons/subjects`);
      if (res.ok) {
        const data = await res.json();
        setSubjects(data.subjects || []);
      }
    } catch (err) {
      console.error('Failed to load subjects', err);
    }
  };

  const handleCreateSubject = async () => {
    const name = newSubject.trim();
    if (!name) return;
    try {
      const res = await fetch(`${API_BASE}/lessons/subjects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...adminAuthHeaders() },
        body: JSON.stringify({ name }),
      });
      if (res.ok) {
        const data = await res.json();
        setSubjects((prev) => [...prev, { id: data.id, name: data.name }]);
        setSubject(data.id, data.name);
        setNewSubject('');
        toast.success('Subject created');
      } else {
        toast.error('Failed to create subject');
      }
    } catch (err) {
      toast.error('Failed to create subject');
    }
  };

  const handleRenameSubject = async () => {
    if (!subjectId) return;
    const name = prompt('Rename subject to:', subjectName || '');
    if (!name) return;
    try {
      const res = await fetch(`${API_BASE}/lessons/subjects/${subjectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...adminAuthHeaders() },
        body: JSON.stringify({ name }),
      });
      if (res.ok) {
        const data = await res.json();
        setSubjects((prev) => prev.map((s) => (s.id === subjectId ? { ...s, name: data.name } : s)));
        setSubject(subjectId, data.name);
        toast.success('Subject renamed');
      } else {
        toast.error('Rename failed');
      }
    } catch (err) {
      toast.error('Rename failed');
    }
  };

  const addYear = () => {
    const val = parseInt(yearInput, 10);
    if (!isNaN(val) && val > 0 && !applicableYears.includes(val)) {
      setApplicableYears([...applicableYears, val].sort((a, b) => a - b));
      setYearInput('');
    }
  };

  const addLabel = () => {
    const val = labelInput.trim();
    if (val && !labels.includes(val)) {
      setLabels([...labels, val]);
      setLabelInput('');
    }
  };

  const handleSave = async () => {
    try {
      await saveDraft();
      toast.success('Lesson draft saved');
    } catch {
      toast.error('Save failed');
    }
  };

  const handlePublish = async () => {
    if (hasErrors) {
      toast.error('Fix validation issues before publishing.');
      return;
    }
    try {
      await publishLesson();
      toast.success('Lesson published');
    } catch (e: any) {
      toast.error(e?.message || 'Publish failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/lessons')}
            className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800"
            title="Back to lessons library"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="p-2 rounded-xl bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200">
            <BookOpen className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xl font-semibold text-gray-900 dark:text-gray-50">Lesson Builder</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {isEditing ? `Editing lesson #${editingLessonId}` : 'Create and preview mobile-first lessons'}
            </div>
            <div className="flex items-center gap-2 mt-1 text-xs">
              <span className="px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200">
                {isEditing ? 'Existing lesson' : 'New draft'}
              </span>
              {loadingLesson && (
                <span className="flex items-center gap-1 text-blue-600 dark:text-blue-300">
                  <RefreshCw className="w-3 h-3 animate-spin" /> Loading…
                </span>
              )}
              {loadError && (
                <span className="text-red-600 dark:text-red-400">Load failed — try refresh</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && !loadingLesson && (
            <div className="text-xs text-orange-600 flex items-center gap-1">
              <RefreshCw className="w-3 h-3 animate-spin" />
              Unsaved changes
            </div>
          )}
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={loadingLesson || isSaving || pages.length === 0}
              className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm font-semibold transition-colors ${
                loadingLesson || isSaving || pages.length === 0
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Draft
            </button>
            <button
              onClick={handlePublish}
              disabled={loadingLesson || isPublishing || pages.length === 0 || hasErrors}
              className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm font-semibold transition-colors ${
                loadingLesson || isPublishing || pages.length === 0 || hasErrors
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {isPublishing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Publish
            </button>
          </div>
        </div>

        {hasErrors && (
          <div className="mb-4 rounded-2xl border border-amber-300 bg-amber-50 text-amber-900 p-3 text-sm space-y-1">
            <div className="font-semibold">Please fix before publishing:</div>
            <ul className="list-disc list-inside space-y-1">
              {validation.errors.slice(0, 6).map((err, idx) => (
                <li key={idx}>{err}</li>
              ))}
              {validation.errors.length > 6 && (
                <li>...and {validation.errors.length - 6} more</li>
              )}
            </ul>
          </div>
        )}
      </div>

      <div className="border border-gray-200 dark:border-gray-800 rounded-3xl bg-gray-50 dark:bg-gray-950 p-4">
        <div className="flex items-center gap-3 mb-4">
          <input
            className="w-full md:w-2/3 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-4 py-3 text-lg font-semibold"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Lesson title"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400">Subject</label>
            <div className="flex gap-2">
              <select
                className="flex-1 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                value={subjectId || ''}
                onChange={(e) => {
                  const id = e.target.value ? Number(e.target.value) : null;
                  const subj = subjects.find((s) => s.id === id);
                  setSubject(id, subj?.name);
                }}
              >
                <option value="">Select subject</option>
                {subjects.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              <button
                className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm"
                onClick={handleRenameSubject}
                disabled={!subjectId}
              >
                Rename
              </button>
            </div>
            <div className="flex gap-2 mt-2">
              <input
                className="flex-1 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                placeholder="New subject name"
                value={newSubject}
                onChange={(e) => setNewSubject(e.target.value)}
              />
              <button
                className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm"
                onClick={handleCreateSubject}
              >
                Add
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400">Access</label>
            <div className="flex gap-2">
              {(['free', 'premium'] as const).map((level) => (
                <button
                  key={level}
                  className={`px-3 py-2 rounded-lg text-sm border ${accessLevel === level ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-200 dark:border-gray-700'}`}
                  onClick={() => setAccessLevel(level)}
                >
                  {level === 'free' ? 'Free' : 'Premium'}
                </button>
              ))}
            </div>
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Premium lessons require an active subscription.
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400">Curriculum</label>
            <div className="flex flex-wrap gap-2">
              {(['ncert', 'icse', 'general', 'misc'] as const).map((type) => (
                <button
                  key={type}
                  className={`px-3 py-2 rounded-lg text-sm border ${curriculumType === type ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-200 dark:border-gray-700'}`}
                  onClick={() => setCurriculumType(type)}
                >
                  {type.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400">Class level</label>
            <select
              className="w-full rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              value={classLevel ?? ''}
              onChange={(e) => {
                const value = e.target.value ? Number(e.target.value) : null;
                setClassLevel(value);
              }}
              disabled={!(curriculumType === 'ncert' || curriculumType === 'icse')}
            >
              <option value="">Select class</option>
              {Array.from({ length: 8 }, (_, i) => i + 5).map((level) => (
                <option key={level} value={level}>Class {level}</option>
              ))}
            </select>
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Required for NCERT/ICSE lessons.
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400">Chapter title</label>
            <input
              className="w-full rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              placeholder="e.g. Motion in One Dimension"
              value={chapterTitle}
              onChange={(e) => setChapterTitle(e.target.value)}
            />
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Required for all lessons.
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400">Content type</label>
            <div className="flex gap-2">
              {(['static', 'current_affairs', 'both'] as const).map((c) => (
                <button
                  key={c}
                  className={`px-3 py-2 rounded-lg text-sm border ${category === c ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-200 dark:border-gray-700'}`}
                  onClick={() => setCategory(c)}
                >
                  {c === 'static' ? 'Static' : c === 'current_affairs' ? 'Current Affairs' : 'Both'}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400">Difficulty</label>
            <select
              className="w-full rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value as any)}
            >
              <option value="beginner">Beginner</option>
              <option value="medium">Medium</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400">Applicable years</label>
            <div className="flex gap-2">
              <input
                className="flex-1 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                placeholder="e.g. 2025"
                value={yearInput}
                onChange={(e) => setYearInput(e.target.value)}
              />
              <button className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-sm" onClick={addYear}>
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {applicableYears.map((yr) => (
                <span key={yr} className="px-2 py-1 rounded-full text-xs bg-gray-200 dark:bg-gray-800">
                  {yr}
                  <button
                    className="ml-2 text-red-500"
                    onClick={() => setApplicableYears(applicableYears.filter((y) => y !== yr))}
                  >
                    ×
                  </button>
                </span>
              ))}
              {applicableYears.length === 0 && <span className="text-xs text-gray-500">All years</span>}
            </div>
            <label className="mt-2 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                checked={appliesUntilReplaced}
                onChange={(e) => setAppliesUntilReplaced(e.target.checked)}
              />
              Applicable until replaced
            </label>
          </div>

          <div className="col-span-1 md:col-span-2 xl:col-span-1">
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400">Labels</label>
            <div className="flex gap-2">
              <input
                className="flex-1 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                placeholder="e.g. NCERT"
                value={labelInput}
                onChange={(e) => setLabelInput(e.target.value)}
              />
              <button className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-sm" onClick={addLabel}>
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {labels.map((l) => (
                <span key={l} className="px-2 py-1 rounded-full text-xs bg-gray-200 dark:bg-gray-800">
                  {l}
                  <button className="ml-2 text-red-500" onClick={() => setLabels(labels.filter((x) => x !== l))}>
                    ×
                  </button>
                </span>
              ))}
              {labels.length === 0 && <span className="text-xs text-gray-500">No labels</span>}
            </div>
          </div>
        </div>

        {selectedPage && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400">Page title</label>
              <input
                className="w-full rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                value={selectedPage.title || ''}
                onChange={(e) => updatePage(selectedPage.id, { title: e.target.value })}
                placeholder="Optional page label"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400">CTA label</label>
              <input
                className="w-full rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                value={selectedPage.cta_label || ''}
                onChange={(e) => updatePage(selectedPage.id, { cta_label: e.target.value })}
                placeholder="Next / Finish"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400">Duration hint (sec)</label>
              <input
                type="number"
                min={0}
                className="w-full rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                value={selectedPage.duration_hint ?? ''}
                onChange={(e) => updatePage(selectedPage.id, { duration_hint: Number(e.target.value) || undefined })}
                placeholder="e.g. 30"
              />
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={selectedPage.is_intro || false}
                  onChange={(e) => updatePage(selectedPage.id, { is_intro: e.target.checked })}
                />
                Intro
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={selectedPage.is_summary || false}
                  onChange={(e) => updatePage(selectedPage.id, { is_summary: e.target.checked })}
                />
                Summary
              </label>
            </div>
          </div>
        )}

        <div className="flex flex-col xl:flex-row gap-4">
          <div className="flex flex-col gap-4 w-full xl:w-auto">
            <PageRail />
            <BlockLibrary />
          </div>
          <LessonCanvas />
          <BlockEditor />
        </div>
      </div>
    </div>
  );
};

export default LessonBuilder;
